import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getStudents, deleteStudent } from '../../services/studentService';
import { getSchools } from '../../services/schoolService';
import { getUsers } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import Loader from '../common/Loader';
import ConfirmModal from '../common/ConfirmModal';
import Alert from '../common/Alert';
import ProfileAvatar from '../common/ProfileAvatar';
import TableActionMenu from '../common/TableActionMenu';
import { formatDisplayDate } from '../../utils/dateUtils';
const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const TEACHER_ROLE_ID = 2;
const STUDENTS_LIST_STORAGE_KEY = 'athenian_students_list_state';

const EMPTY_FILTERS = { search: '', grade: '', school_id: '', sped_teacher_id: '' };

function parsePageParam(raw) {
  const n = parseInt(raw || '1', 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

function filtersFromSearchParams(searchParams) {
  return {
    search: searchParams.get('search') || '',
    grade: searchParams.get('grade') || '',
    school_id: searchParams.get('school_id') || '',
    sped_teacher_id: searchParams.get('sped_teacher_id') || '',
  };
}

function readStoredListState() {
  try {
    const raw = sessionStorage.getItem(STUDENTS_LIST_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      page: parsePageParam(parsed.page),
      filters: { ...EMPTY_FILTERS, ...(parsed.filters || {}) },
    };
  } catch {
    return null;
  }
}

function writeStoredListState(page, filters) {
  try {
    sessionStorage.setItem(STUDENTS_LIST_STORAGE_KEY, JSON.stringify({ page, filters }));
  } catch { /* ignore quota / private mode */ }
}

function resolveInitialListState(searchParams) {
  const urlPage = searchParams.get('page');
  const urlFilters = filtersFromSearchParams(searchParams);
  const hasUrlFilters = Object.values(urlFilters).some(Boolean);
  if (urlPage || hasUrlFilters) {
    return { page: parsePageParam(urlPage), filters: urlFilters };
  }
  const stored = readStoredListState();
  if (stored) return stored;
  return { page: 1, filters: EMPTY_FILTERS };
}

function buildListSearchParams(page, filters) {
  const params = new URLSearchParams();
  if (page > 1) params.set('page', String(page));
  if (filters.search) params.set('search', filters.search);
  if (filters.grade) params.set('grade', filters.grade);
  if (filters.school_id) params.set('school_id', filters.school_id);
  if (filters.sped_teacher_id) params.set('sped_teacher_id', filters.sped_teacher_id);
  return params;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return formatDisplayDate(iso);
  } catch { return '—'; }
}

function complianceColor(score) {
  if (score === null || score === undefined) return 'text-gray-400 dark:text-slate-500';
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 50) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function exportToCSV(students) {
  const headers = [
    'Student ID', 'Student Name', 'Grade', 'School', 'SPED Teacher', 'Date of Birth',
    'IEP Due Date', 'Reevaluation Due Date', 'Compliance Score', 'Last Updated',
  ];
  const rows = students.map((s) => [
    s.student_id || '',
    s.student_name || '',
    s.grade || '',
    s.school_name || '',
    s.sped_teacher?.name || s.case_manager || '',
    s.date_of_birth ? formatDate(s.date_of_birth) : '',
    s.iep_due_date ? formatDate(s.iep_due_date) : '',
    s.reevaluation_due_date ? formatDate(s.reevaluation_due_date) : '',
    s.compliance_score ?? '',
    s.last_updated ? formatDate(s.last_updated) : '',
  ]);
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `students_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function StudentList() {
  const { user: currentUser } = useAuth();
  const isDirector = currentUser?.role === 1;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialListStateRef = useRef(null);
  if (initialListStateRef.current == null) {
    initialListStateRef.current = resolveInitialListState(searchParams);
  }
  const initialListState = initialListStateRef.current;

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  const [pagination, setPagination] = useState({
    total: 0,
    page: initialListState.page,
    limit: 10,
    total_pages: 0,
  });

  const [filters, setFilters] = useState(initialListState.filters);
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const persistListState = useCallback((page, nextFilters) => {
    writeStoredListState(page, nextFilters);
    const params = buildListSearchParams(page, nextFilters);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const fetchStudents = useCallback(async (params = {}, page = 1) => {
    setLoading(true);
    try {
      const queryParams = { page, limit: pagination.limit, ...params };
      Object.keys(queryParams).forEach((k) => {
        if (queryParams[k] === '' || queryParams[k] === null || queryParams[k] === undefined)
          delete queryParams[k];
      });
      const { data } = await getStudents(queryParams);
      let payload = data;
      let resolvedPage = page;
      let total = payload.data?.total || 0;
      let totalPages = payload.data?.total_pages || 0;
      // A saved page (for example page=2) stays in the URL after students are
      // removed. Snap back so the list is not empty while earlier pages have rows.
      if (page > 1 && (total === 0 || page > totalPages)) {
        resolvedPage = total === 0 ? 1 : Math.max(1, totalPages);
        persistListState(resolvedPage, params);
        if (total > 0 && resolvedPage !== page) {
          const retryParams = { ...queryParams, page: resolvedPage };
          const retry = await getStudents(retryParams);
          payload = retry.data;
          total = payload.data?.total || 0;
          totalPages = payload.data?.total_pages || 0;
        }
      }
      setStudents(payload.data?.students || []);
      setPagination({
        total,
        page: payload.data?.page || resolvedPage,
        limit: payload.data?.limit || 10,
        total_pages: totalPages,
      });
    } catch {
      setError('Failed to load students.');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, persistListState]);

  useEffect(() => {
    const { page, filters: f } = initialListStateRef.current;
    persistListState(page, f);
    fetchStudents(f, page);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isDirector) return;
    getSchools({ page: 1, limit: 100, status: 'active' })
      .then(({ data }) => setSchools(data.data?.schools || []))
      .catch(() => {});
  }, [isDirector]);

  useEffect(() => {
    if (!isDirector) return;
    const params = {
      role: TEACHER_ROLE_ID,
      status: 'active',
      page: 1,
      limit: 100,
    };
    if (filters.school_id) params.school_id = parseInt(filters.school_id, 10);
    getUsers(params)
      .then(({ data }) => setTeachers(data.data?.users || []))
      .catch(() => setTeachers([]));
  }, [isDirector, filters.school_id]);

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    if (key === 'school_id') updated.sped_teacher_id = '';
    setFilters(updated);
    clearTimeout(debounceRef.current);
    if (key === 'grade' || key === 'school_id' || key === 'sped_teacher_id') {
      persistListState(1, updated);
      fetchStudents(updated, 1);
    } else {
      debounceRef.current = setTimeout(() => {
        persistListState(1, updated);
        fetchStudents(updated, 1);
      }, 400);
    }
  };

  const clearFilters = () => {
    const cleared = { ...EMPTY_FILTERS };
    setFilters(cleared);
    persistListState(1, cleared);
    fetchStudents(cleared, 1);
  };

  const toggleMenu = (id) => setOpenMenuId((prev) => (prev === id ? null : id));

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteStudent(confirmDelete);
      setAlert({ type: 'success', message: 'Student deleted successfully.' });
      setConfirmDelete(null);
      fetchStudents(filters, pagination.page);
    } catch {
      setAlert({ type: 'error', message: 'Failed to delete student.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    if (students.length === 0) {
      setAlert({ type: 'error', message: 'No students to export on this page.' });
      return;
    }
    exportToCSV(students);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    persistListState(page, filters);
    fetchStudents(filters, page);
  };

  const hasActiveFilters = filters.search || filters.grade || filters.school_id || filters.sped_teacher_id;

  const renderActionMenu = (s) => (
    <TableActionMenu
      isOpen={openMenuId === s.id}
      onToggle={() => toggleMenu(s.id)}
      onClose={() => setOpenMenuId(null)}
    >
      <button
        type="button"
        onClick={() => { setOpenMenuId(null); navigate(`/students/${s.id}/information`); }}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        View Details
      </button>
      <button
        type="button"
        onClick={() => { setOpenMenuId(null); navigate(`/students/${s.id}/edit`); }}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
        Edit Student
      </button>
      <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
      <button
        type="button"
        onClick={() => { setOpenMenuId(null); setConfirmDelete(s.id); }}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        Delete Student
      </button>
    </TableActionMenu>
  );

  return (
    <div className="space-y-5 min-w-0">
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Delete Student"
        message="Are you sure you want to delete this student? This action cannot be undone."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        loading={deleting}
        variant="danger"
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Students</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
            Manage SPED student records and IEP tracking
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto">
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export
          </button>
          <Link
            to="/students/add"
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Student
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isDirector ? 'lg:grid-cols-3 xl:grid-cols-5' : 'lg:grid-cols-2'} gap-2.5 items-center`}>
        <div className={`relative min-w-0 ${isDirector ? 'sm:col-span-2 lg:col-span-1 xl:col-span-1' : 'sm:col-span-2'}`}>
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name or student ID..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm transition-colors"
          />
        </div>

        <select
          value={filters.grade}
          onChange={(e) => handleFilterChange('grade', e.target.value)}
          className="w-full min-w-0 px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
        >
          <option value="">All Grades</option>
          {GRADES.map((g) => <option key={g} value={g}>Grade {g}</option>)}
        </select>

        {isDirector && (
          <>
            <select
              value={filters.school_id}
              onChange={(e) => handleFilterChange('school_id', e.target.value)}
              className="w-full min-w-0 px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
            >
              <option value="">All Schools</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>{s.school_name}</option>
              ))}
            </select>

            <select
              aria-label="SPED teacher"
              value={filters.sped_teacher_id}
              onChange={(e) => handleFilterChange('sped_teacher_id', e.target.value)}
              className="w-full min-w-0 px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
            >
              <option value="">All SPED Teachers</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {[t.first_name, t.last_name].filter(Boolean).join(' ')}
                </option>
              ))}
            </select>
          </>
        )}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center justify-center w-full sm:w-9 h-9 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:text-red-400 dark:hover:border-red-500/40 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors sm:justify-self-start"
            title="Clear all filters"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Table / cards */}
      <div className="bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-2xl min-w-0">
        {error ? (
          <div className="px-4 sm:px-6 py-12 text-center text-red-500 text-sm">{error}</div>
        ) : loading ? (
          <Loader text="Loading students..." />
        ) : students.length === 0 ? (
          <div className="px-4 sm:px-6 py-12 sm:py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5zm0 7l9-5-9-5-9 5 9 5z" />
              </svg>
            </div>
            <p className="text-gray-900 dark:text-white font-semibold">No students found</p>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-700">
              {students.map((s) => (
                <div key={s.id} className="px-3 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/students/${s.id}/information`} className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0">
                        <ProfileAvatar
                          type="student"
                          id={s.id}
                          variant="light"
                          hasProfileImage={s.has_profile_image}
                          fetchImage={!!s.has_profile_image}
                          lazyImage
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{s.student_name}</p>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">
                          ID: {s.student_id || '—'} · Grade {s.grade || '—'}
                        </p>
                      </div>
                    </Link>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-sm font-bold whitespace-nowrap ${complianceColor(s.compliance_score)}`}>
                        {s.compliance_score !== null && s.compliance_score !== undefined ? `${s.compliance_score}%` : '—'}
                      </span>
                      {renderActionMenu(s)}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-1 text-[11px] text-gray-500 dark:text-slate-400 pl-12">
                    <p className="truncate"><span className="text-gray-400 dark:text-slate-500">School:</span> {s.school_name || '—'}</p>
                    <p className="truncate"><span className="text-gray-400 dark:text-slate-500">Teacher:</span> {s.sped_teacher?.name || s.case_manager || '—'}</p>
                    {(s.iep_due_date || s.reevaluation_due_date) && (
                      <p className="truncate">
                        {s.iep_due_date && <>IEP: {formatDate(s.iep_due_date)}</>}
                        {s.iep_due_date && s.reevaluation_due_date && ' · '}
                        {s.reevaluation_due_date && <>Re-eval: {formatDate(s.reevaluation_due_date)}</>}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto max-w-full min-w-0">
              <table className="w-full min-w-[880px]">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/80">
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Student</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">Student ID</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Grade</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">School</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">SPED Teacher</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">DOB</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">IEP Due</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">Re-eval Due</th>
                    <th className="px-3 lg:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">Compliance</th>
                    <th className="sticky right-0 z-20 px-3 lg:px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-20 min-w-[5rem] bg-gray-50/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] dark:shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.35)]">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60">
                  {students.map((s) => (
                    <tr key={s.id} className="group hover:bg-gray-50/60 dark:hover:bg-slate-700/20 transition-colors">
                      <td className="px-3 lg:px-5 py-3 lg:py-4">
                        <Link to={`/students/${s.id}/information`} className="flex items-center gap-3 group min-w-0 max-w-[200px] lg:max-w-none">
                          <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0">
                            <ProfileAvatar
                              type="student"
                              id={s.id}
                              variant="light"
                              hasProfileImage={s.has_profile_image}
                              fetchImage={!!s.has_profile_image}
                              lazyImage
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-gray-900 dark:text-white font-semibold text-sm truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                              {s.student_name}
                            </p>
                            <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate lg:hidden">
                              {s.student_id || '—'}
                            </p>
                          </div>
                        </Link>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4 hidden lg:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-xs font-mono whitespace-nowrap">{s.student_id || '—'}</span>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4">
                        <span className="inline-flex px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 text-xs font-medium border border-slate-200 dark:border-slate-600 whitespace-nowrap">
                          Grade {s.grade}
                        </span>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4">
                        <span className="text-gray-600 dark:text-slate-300 text-sm truncate block max-w-[120px] lg:max-w-[160px]">{s.school_name || '—'}</span>
                        <span className="text-[11px] text-gray-400 dark:text-slate-500 truncate block max-w-[120px] lg:hidden">
                          {s.sped_teacher?.name || s.case_manager || '—'}
                        </span>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4 hidden lg:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-sm truncate block max-w-[160px]">
                          {s.sped_teacher?.name || s.case_manager || '—'}
                        </span>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4 hidden xl:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-sm whitespace-nowrap">{formatDate(s.date_of_birth)}</span>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4 hidden xl:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-sm whitespace-nowrap">{formatDate(s.iep_due_date)}</span>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4 hidden xl:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-sm whitespace-nowrap">{formatDate(s.reevaluation_due_date)}</span>
                      </td>
                      <td className="px-3 lg:px-5 py-3 lg:py-4">
                        <span className={`text-sm font-bold whitespace-nowrap ${complianceColor(s.compliance_score)}`}>
                          {s.compliance_score !== null && s.compliance_score !== undefined ? `${s.compliance_score}%` : '—'}
                        </span>
                      </td>
                      <td className="sticky right-0 z-10 px-3 lg:px-4 py-3 lg:py-4 text-center bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm group-hover:bg-gray-50/95 dark:group-hover:bg-slate-700/40 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] dark:shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.35)]">
                        {renderActionMenu(s)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-3 sm:px-5 py-3 sm:py-4 border-t border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Showing{' '}
                  <span className="font-medium text-gray-700 dark:text-slate-300">
                    {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium text-gray-700 dark:text-slate-300">{pagination.total}</span>
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let page;
                    if (pagination.total_pages <= 5) page = i + 1;
                    else if (pagination.page <= 3) page = i + 1;
                    else if (pagination.page >= pagination.total_pages - 2) page = pagination.total_pages - 4 + i;
                    else page = pagination.page - 2 + i;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                          page === pagination.page
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                            : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}